import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, Phone, User, Accessibility, FileSignature } from 'lucide-react';

/**
 * Loads the signed disclaimer for a booking and surfaces the emergency
 * contact + accessibility needs so staff can prepare for the session.
 * Only fires the query when the parent booking row is expanded.
 */
export default function BookingDisclaimerPanel({ bookingId, customerEmail }) {
  const { data: disclaimers = [], isLoading } = useQuery({
    queryKey: ['booking-disclaimer', bookingId],
    queryFn: () =>
      base44.entities.Disclaimer.filter(
        { customer_email: customerEmail || '' },
        '-created_date',
        10
      ),
    enabled: !!bookingId,
  });

  // booking_ids is a comma-separated string — match the expanded booking's id
  const matched = disclaimers.find((d) => {
    const ids = (d.booking_ids || '').split(',').map((s) => s.trim()).filter(Boolean);
    return ids.includes(bookingId);
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-white/30 text-xs pt-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading disclaimer…
      </div>
    );
  }

  if (!matched) {
    return (
      <div className="border border-white/5 bg-[#0d0d0f] px-4 py-3 mt-1">
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">DISCLAIMER</p>
        <p className="text-white/40 text-xs">No signed disclaimer on file for this booking.</p>
      </div>
    );
  }

  return (
    <div className="border border-white/5 bg-[#0d0d0f] px-4 py-3 mt-1 space-y-3">
      <div className="flex items-center gap-2">
        <FileSignature className="w-3.5 h-3.5 text-primary" />
        <p className="text-primary text-[10px] font-bold tracking-widest">SIGNED DISCLAIMER</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-start gap-2">
          <User className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest">EMERGENCY CONTACT</p>
            <p className="text-white/60 text-xs">{matched.emergency_contact_name || '—'}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Phone className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest">EMERGENCY PHONE</p>
            <p className="text-white/60 text-xs">{matched.emergency_contact_phone || '—'}</p>
          </div>
        </div>
      </div>

      {matched.signed_as_guardian && (
        <p className="text-white/40 text-[11px]">Signed as parent/guardian on behalf of a user under 16.</p>
      )}

      {matched.accessibility_needs && (
        <div className="flex items-start gap-2 pt-2 border-t border-white/5">
          <Accessibility className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">ACCESSIBILITY & SUPPORT</p>
            <p className="text-white/70 text-xs leading-relaxed mt-0.5">{matched.accessibility_needs}</p>
          </div>
        </div>
      )}
    </div>
  );
}